import React, { PureComponent } from 'react'
import getLogger from '@signal/core/fe-logging'
import { AccountGuidError } from '@signal/utils/errors'
import isValidGuid from '@signal/utils/isValidGuid'
import { getLoginUrl } from '@signal/core/auth/auth'
import UI_BASE_PATH from '@signal/core/baseline/app/base-path'
import { getAcctSelectorUrl } from '@signal/core/router/redirect'
import { ThemeProvider } from '@material-ui/core'
import { makeLightTheme } from '@signal/theme/theme'
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react'
import UnexpectedError from '@signal/components/UnexpectedError'
import isDevelopment from '@signal/utils/env/isDevelopment'

class ErrorBoundary extends PureComponent {
  lightTheme = undefined
  state = { hasError: false }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static getDerivedStateFromError(error) {
    return { hasError: true }
  }

  constructor(...args) {
    super(...args)
    this.lightTheme = makeLightTheme()
  }

  componentDidCatch(error, { componentStack }) {
    // handle uncaught auth errors for apps without default middleware
    if (error.response && error.response.status === 401) {
      window.location.replace(getLoginUrl())
    }

    // log exception
    getLogger().exception(error, componentStack)

    if (!isDevelopment) {
      setTimeout(() => {
        const { redirectUrl = undefined } = this.props
        if (redirectUrl) {
          window.location.replace(redirectUrl)
        }
      }, 3000)
    }
  }

  render() {
    const { accountId } = this.props.children.props
    const { redirectUrl = undefined } = this.props
    if (accountId && !isValidGuid(accountId)) {
      throw new AccountGuidError()
    }

    if (this.state.hasError) {
      return (
        <ThemeProvider theme={this.lightTheme}>
          <EmotionThemeProvider theme={this.lightTheme}>
            <UnexpectedError willRedirect={redirectUrl} />
          </EmotionThemeProvider>
        </ThemeProvider>
      )
    }

    return this.props.children
  }
}

function withRedirect(WrappedComponent, props) {
  const { redirectPath } = props
  const redirectUrl = `${getAcctSelectorUrl(`${UI_BASE_PATH}${redirectPath}`)}`

  return <WrappedComponent redirectUrl={redirectUrl} {...props} />
}
/**
 *
 * @param {string} redirectPath A string that contains a path to be redirected to after account selection
 * @returns
 */
export const ThemeSafeErrorBoundary = props =>
  withRedirect(ErrorBoundary, props)
